const BRIDGE_SRC = 'https://app.storyblok.com/f/storyblok-v2-latest.js'

export interface BridgePayload {
  action?: string
  storyId?: number
  story?: { id?: number; content?: unknown }
}

interface BridgeWindow {
  StoryblokBridge?: new () => { on: (events: string[], callback: (payload: BridgePayload) => void) => void }
  __sbLiveArmed?: boolean
  __sbLiveHandler?: (payload: BridgePayload) => void
  __sbLiveLoading?: Promise<void>
}

const loadBridge = (target: BridgeWindow): Promise<void> => {
  if (target.StoryblokBridge) {
    return Promise.resolve()
  }
  if (target.__sbLiveLoading) {
    return target.__sbLiveLoading
  }
  target.__sbLiveLoading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = BRIDGE_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      script.remove()
      reject(new Error('Failed to load Storyblok bridge'))
    }
    document.head.appendChild(script)
  }).catch((error) => {
    target.__sbLiveLoading = undefined
    throw error
  })
  return target.__sbLiveLoading
}

// The bridge has no off(). Keep one subscription per document (also across
// HMR), and let each effect own and clean up its dispatcher independently.
export const subscribeLivePreview = (handler: (payload: BridgePayload) => void): (() => void) => {
  const target = window as BridgeWindow
  target.__sbLiveHandler = handler
  const arm = () => {
    if (target.__sbLiveHandler !== handler || target.__sbLiveArmed || !target.StoryblokBridge) {
      return
    }
    new target.StoryblokBridge().on(['input', 'change', 'published', 'unpublished'], (payload) => {
      target.__sbLiveHandler?.(payload)
    })
    target.__sbLiveArmed = true
  }
  if (target.StoryblokBridge) {
    arm()
  } else {
    void loadBridge(target)
      .then(arm)
      .catch((error) => console.error('[sb-live]', error))
  }
  return () => {
    if (target.__sbLiveHandler === handler) {
      target.__sbLiveHandler = undefined
    }
  }
}
